import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Drawer, Form, Modal, Select, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import { getInstitutionCategories } from "../../services/institutionCategory";
import ImageFileUpload from "../../components/Inputs/ImageFileUpload";
import { VITE_API_BASE_URL } from "../../config";
import { getInstitutions } from "../../services/institutionService";
import {
  createNotableAlumnus,
  deleteNotableAlumnus,
  getNotableAlumni,
  updateNotableAlumnus,
} from "../../services/notableAlumniService";
import { notableAlumnusSchema } from "../../validators/notableAlumnusValidators";
import TextArea from "../../components/Inputs/TextAreaInput";
import { Link } from "react-router-dom";
import { BiShow } from "react-icons/bi";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const NotableAlumnus = () => {
  const [alumni, setAlumni] = useState([]);
  const [filteredAlumni, setFilteredAlumni] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [alumnusToEdit, setAlumnusToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [alumnusToDelete, setAlumnusToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");

  const [selectedAlumnus, setSelectedAlumnus] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { user } = useStateContext();
  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadNotableAlumni();
    loadInstitutions();
  }, []);

  useEffect(() => {
    let filtered = alumni;
    if (searchTerm.trim() !== "") {
      filtered = alumni.filter((item) =>
        `${item?.first_name} ${item?.last_name} ${
          item?.institution?.name || ""
        }`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    setFilteredAlumni(filtered);
  }, [searchTerm, alumni, selectedInstitution]);

  const loadInstitutions = async () => {
    setLoading(true);
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load institutions"
      );
    } finally {
      setLoading(false);
    }
  };

  const loadNotableAlumni = async () => {
    setLoading(true);
    try {
      const { data } = await getNotableAlumni();
      setAlumni(data.data);
      setFilteredAlumni(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load alumni");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (alumnusToEdit) {
      onUpdateAlumnus(payload, resetForm);
    } else {
      onCreateAlumnus(payload, resetForm);
    }
  };

  const onCreateAlumnus = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createNotableAlumnus(payload);

      setAlumni((prev) => [...prev, data.data]);

      toast.success("Alumnus added successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response.data.error || "Alumnus was not added, try again!");
      }
    }
  };

  const handleEdit = (category) => {
    setAlumnusToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdateAlumnus = async (payload, resetForm) => {
    setErrs(null);

    const formData = new FormData();

    formData.append("_method", "PATCH");
    formData.append("first_name", payload.first_name);
    formData.append("last_name", payload.last_name);
    formData.append("description", payload.description);
    formData.append("achievements", payload.achievements);
    formData.append("institution_id", payload.institution_id);

    if (typeof payload.photo !== "string") {
      formData.append("photo", payload.photo);
    }

    try {
      const originalAlumni = [...alumni];

      const alumnusIndex = originalAlumni.findIndex(
        (alumnus) => alumnus.id === alumnusToEdit.id
      );

      if (alumnusIndex === -1) throw new Error("Alumnus not found.");

      const { data } = await updateNotableAlumnus(alumnusToEdit.id, formData);

      originalAlumni[alumnusIndex] = data.data;

      setAlumni(originalAlumni);

      setAlumnusToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Alumnus details updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Alumnus details not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setAlumnusToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteAlumnus = async () => {
    const originalAlumni = [...alumni];

    const newAlumni = originalAlumni.filter(
      (alumnus) => alumnus.id !== alumnusToDelete.id
    );

    setAlumni(newAlumni);

    try {
      await deleteNotableAlumnus(alumnusToDelete?.id);

      toast.success("Alumnus deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Alumnus not deleted, try again later"
        }`
      );

      setAlumni(originalAlumni);
    } finally {
      setIsDeleteModalOpen(false);

      setAlumnusToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setAlumnusToEdit(null);
    setAlumnusToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    {
      title: "Name",
      key: "name",
      render: (_, record) => (
        <span className="text-gray-700 text-sm">
          {`${record.first_name} ${record.last_name}`}
        </span>
      ),
    },

    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "center", color: "#888" }}>
            {displayText}
          </div>
        ) : (
          <>{displayText}</>
        );
      },
    },
    {
      title: "Achievements",
      dataIndex: "achievements",
      key: "achievements",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "center", color: "#888" }}>
            {displayText}
          </div>
        ) : (
          <>{displayText}</>
        );
      },
    },
    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: ["institution", "name"],
            key: "institution",
            render: (text) => (
              <span className="text-gray-700 text-sm">{text || "N/A"}</span>
            ),
          },
        ]
      : []),
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Alumnus">
            <button
              className="p-2 border border-blue-200 rounded-md text-blue-600 hover:bg-blue-50 transition"
              onClick={() => {
                setSelectedAlumnus(record);
                setIsDrawerOpen(true);
              }}
            >
              <BiShow size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Edit Alumnus">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Alumnus">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Notable Alumni" subtitle1="Notable Alumni" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Notable Alumni"
            count={filteredAlumni.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Alumnus"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredAlumni.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              <div className="mb-2 py-2 flex gap-2">
                <Select
                  multiple={false}
                  id="institutions"
                  defaultValue={selectedInstitution}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => {
                    setSelectedInstitution(value);
                  }}
                  showSearch={true}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  <Select.Option value={"Not Selected"}>
                    Filter by Institution
                  </Select.Option>
                  {institutions &&
                    institutions.length > 0 &&
                    institutions.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                </Select>
              </div>
            }
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {alumnusToEdit ? "Edit Alumnus" : "Add Alumnus"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
            width={900}
          >
            <Formik
              initialValues={{
                first_name: alumnusToEdit ? alumnusToEdit?.first_name : "",
                last_name: alumnusToEdit ? alumnusToEdit?.last_name : "",
                description: alumnusToEdit ? alumnusToEdit?.description : "",
                achievements: alumnusToEdit ? alumnusToEdit?.achievements : "",
                photo: alumnusToEdit ? alumnusToEdit?.photo : "",

                institution_id: alumnusToEdit
                  ? alumnusToEdit?.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
              }}
              enableReinitialize
              validationSchema={notableAlumnusSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                submitForm,
                setFieldValue,
                touched,
              }) => {
                return (
                  <Form className="space-y-2 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    {isInstAdmin ? (
                      <input
                        type="hidden"
                        name="institution_id"
                        value={
                          alumnusToEdit
                            ? alumnusToEdit?.institution?.id
                            : user?.institution?.id
                        }
                      />
                    ) : (
                      <CustomDropDownInput
                        name="institution_id"
                        title="Institution"
                        placeholder="Select an institution"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={institutions}
                        setFieldValue={setFieldValue}
                      />
                    )}

                    <div className="grid grid-cols-1">
                      {alumnusToEdit?.logo && (
                        <div className="flex justify-center items-center w-full">
                          <div className="h-40 w-40">
                            <img
                              className="w-54 text-sm"
                              src={`${VITE_API_BASE_URL}/files/logos/${alumnusToEdit?.logo}`}
                              alt={`Photo`}
                            />
                          </div>
                        </div>
                      )}
                      <ImageFileUpload
                        title="Attach Alumnus Photo"
                        name="photo"
                        requiredField={false}
                        errors={errors}
                        acceptedFiles="JPG, PNG up to 2 MB"
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                      />
                    </div>

                    <div className="grid gap-x-3 grid-cols-1 md:grid-cols-2 ">
                      <TextInput
                        name="first_name"
                        title="First Name"
                        placeholder="e.g Kevin"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        autoFocus
                      />

                      <TextInput
                        name="last_name"
                        title="Last Name"
                        placeholder="e.g Kasumba"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        autoFocus
                      />
                    </div>

                    <TextArea
                      name="description"
                      title="Description"
                      placeholder="e.g. A renowned environmentalist advocating for sustainable agriculture in East Africa."
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <TextArea
                      name="achievements"
                      title="Achievements"
                      placeholder="e.g. Founded EcoPower Uganda, awarded Forbes 30 Under 30 in Energy."
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteAlumnus}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />

          <Drawer
            title={
              <div className="flex items-center gap-3">
                {selectedAlumnus?.photo && (
                  <img
                    src={`${VITE_API_BASE_URL}/files/alumni/${selectedAlumnus.photo}`}
                    alt="Alumnus"
                    className="h-16 w-16 object-cover rounded-full border"
                  />
                )}
                <div>
                  <h3 className="text-lg font-semibold">
                    {selectedAlumnus?.first_name} {selectedAlumnus?.last_name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedAlumnus?.institution?.name || "—"}
                  </p>
                </div>
              </div>
            }
            placement="right"
            width={500}
            onClose={() => setIsDrawerOpen(false)}
            open={isDrawerOpen}
          >
            {selectedAlumnus ? (
              <div className="space-y-6 text-sm text-gray-700">
                <div>
                  <h4 className="text-sm font-semibold text-gray-600 mb-1">
                    Description
                  </h4>
                  <p>{selectedAlumnus.description || "—"}</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-600 mb-1">
                    Achievements
                  </h4>
                  <p>{selectedAlumnus.achievements || "—"}</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No alumnus selected.</p>
            )}
          </Drawer>
        </div>
      </div>
    </>
  );
};

export default NotableAlumnus;
